/**
 * Unlimited landing menu (Figma 6127-17880). Side drawer on desktop; top sheet on small viewports.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import { useFirebaseAuth } from '../../../../global/FirebaseProvider/FirebaseProvider';
import {
  ROUTE_LOGIN,
  ROUTE_REWARDS_V2,
} from '../../../config/routes';
import {
  FAQS_URL,
  HOW_WE_DONATE_URL,
  TAKE_BACK_BAG_PRODUCTS_URL,
} from '../../../config/externalURL';
import {
  isMobile,
  SIZE_SM,
  SIZE_XS,
} from '../../../../../hooks/useBreakPoint';

import arrowsLeftRightIcon from '../../../images/arrowsleftright.svg';
import menuIcon from '../../../images/menu.svg';
import questionsIcon from '../../../images/questions.svg';
import shopTakeBagIcon from '../../../images/shop-take-bag.svg';
import tcIcon from '../../../images/tc.svg';

import './SubscriptionUnlimitedMenuDrawer.scss';

const PANEL_ID = 'subscription-unlimited-menu-dialog';
const TITLE_ID = 'subscription-unlimited-menu-title';

const MENU_ICON_SRC = {
  shop: shopTakeBagIcon,
  rewards: tcIcon,
  donate: arrowsLeftRightIcon,
  faq: questionsIcon,
};

const MenuRowIcon = ({ mod }) => (
  <img
    className="SubscriptionUnlimitedMenuDrawer__icon-img"
    src={MENU_ICON_SRC[mod]}
    alt=""
    aria-hidden
  />
);

const MENU_ITEMS = [
  {
    id: 'shop',
    label: 'Shop Take Back',
    href: TAKE_BACK_BAG_PRODUCTS_URL,
    external: true,
    iconMod: 'shop',
  },
  {
    id: 'rewards',
    label: 'TrashieCash™ Rewards',
    to: ROUTE_REWARDS_V2,
    iconMod: 'rewards',
  },
  {
    id: 'donate',
    label: 'How We Donate',
    href: HOW_WE_DONATE_URL,
    external: true,
    iconMod: 'donate',
  },
  {
    id: 'faq',
    label: 'Trashie FAQs',
    href: FAQS_URL,
    external: true,
    iconMod: 'faq',
  },
];

const UnlimitedMenuPanelContent = ({
  panelId,
  panelClassName,
  closeBtnRef,
  close,
  showAuthButtons,
}) => (
  <div
    id={panelId}
    role="dialog"
    aria-modal="true"
    aria-labelledby={TITLE_ID}
    className={panelClassName}
  >
    <header className="SubscriptionUnlimitedMenuDrawer__header">
      <h2 id={TITLE_ID} className="SubscriptionUnlimitedMenuDrawer__title">
        Menu
      </h2>
      <button
        ref={closeBtnRef}
        type="button"
        className="SubscriptionUnlimitedMenuDrawer__close"
        aria-label="Close menu"
        onClick={close}
      >
        <svg
          className="SubscriptionUnlimitedMenuDrawer__close-icon"
          viewBox="0 0 24 24"
          width={24}
          height={24}
          aria-hidden
        >
          <path
            d="M18 6L6 18M6 6l12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
          />
        </svg>
      </button>
    </header>

    <nav className="SubscriptionUnlimitedMenuDrawer__nav" aria-label="Main">
      <ul className="SubscriptionUnlimitedMenuDrawer__list">
        {MENU_ITEMS.map((item) => (
          <li key={item.id} className="SubscriptionUnlimitedMenuDrawer__item">
            {item.external ? (
              <a
                className="SubscriptionUnlimitedMenuDrawer__link"
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={close}
              >
                <span
                  className={`SubscriptionUnlimitedMenuDrawer__icon SubscriptionUnlimitedMenuDrawer__icon--${item.iconMod}`}
                >
                  <MenuRowIcon mod={item.iconMod} />
                </span>
                <span className="SubscriptionUnlimitedMenuDrawer__label">{item.label}</span>
              </a>
            ) : (
              <Link
                className="SubscriptionUnlimitedMenuDrawer__link"
                to={item.to}
                onClick={close}
              >
                <span
                  className={`SubscriptionUnlimitedMenuDrawer__icon SubscriptionUnlimitedMenuDrawer__icon--${item.iconMod}`}
                >
                  <MenuRowIcon mod={item.iconMod} />
                </span>
                <span className="SubscriptionUnlimitedMenuDrawer__label">{item.label}</span>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </nav>

    {showAuthButtons && (
      <div className="SubscriptionUnlimitedMenuDrawer__auth">
        <Link
          className="SubscriptionUnlimitedMenuDrawer__btn SubscriptionUnlimitedMenuDrawer__btn--primary"
          to={ROUTE_LOGIN}
          onClick={close}
        >
          Sign Up
        </Link>
        <Link
          className="SubscriptionUnlimitedMenuDrawer__btn SubscriptionUnlimitedMenuDrawer__btn--secondary"
          to={ROUTE_LOGIN}
          onClick={close}
        >
          Log In
        </Link>
      </div>
    )}
  </div>
);

const SubscriptionUnlimitedMenuDrawer = () => {
  const closeBtnRef = useRef(null);
  const triggerRef = useRef(null);
  const wasOpenRef = useRef(false);
  const [open, setOpen] = useState(false);
  const isSmallViewport = isMobile([SIZE_XS, SIZE_SM]);
  const { isSignedIn, isLoading: isLoadingAuth } = useFirebaseAuth();

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const t = window.setTimeout(() => {
      closeBtnRef.current?.focus();
    }, 0);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        close();
      }
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  useEffect(() => {
    if (wasOpenRef.current && !open) {
      triggerRef.current?.focus();
    }
    wasOpenRef.current = open;
  }, [open]);

  const showAuthButtons = !isLoadingAuth && !isSignedIn;

  return (
    <div className="SubscriptionUnlimitedMenuDrawer">
      <button
        ref={triggerRef}
        type="button"
        className="SubscriptionUnlimitedMenuDrawer__trigger"
        aria-expanded={open}
        aria-controls={PANEL_ID}
        onClick={() => setOpen(true)}
      >
        <img
          className="SubscriptionUnlimitedMenuDrawer__trigger-icon"
          src={menuIcon}
          alt=""
          width={24}
          height={24}
          aria-hidden
        />
        <span className="SubscriptionUnlimitedMenuDrawer__sr-only">Open menu</span>
      </button>

      {open && createPortal(
        isSmallViewport ? (
          <div className="SubscriptionUnlimitedMenuDrawer__portal SubscriptionUnlimitedMenuDrawer__portal--mobile">
            <UnlimitedMenuPanelContent
              panelId={PANEL_ID}
              panelClassName="SubscriptionUnlimitedMenuDrawer__panel SubscriptionUnlimitedMenuDrawer__panel--top"
              closeBtnRef={closeBtnRef}
              close={close}
              showAuthButtons={showAuthButtons}
            />
            <button
              type="button"
              className="SubscriptionUnlimitedMenuDrawer__mobile-dismiss"
              aria-label="Close menu"
              onClick={close}
            />
          </div>
        ) : (
          <div className="SubscriptionUnlimitedMenuDrawer__portal SubscriptionUnlimitedMenuDrawer__portal--desktop">
            <button
              type="button"
              className="SubscriptionUnlimitedMenuDrawer__backdrop"
              aria-label="Close menu"
              onClick={close}
            />
            <UnlimitedMenuPanelContent
              panelId={PANEL_ID}
              panelClassName="SubscriptionUnlimitedMenuDrawer__panel SubscriptionUnlimitedMenuDrawer__panel--side"
              closeBtnRef={closeBtnRef}
              close={close}
              showAuthButtons={showAuthButtons}
            />
          </div>
        ),
        document.body,
      )}
    </div>
  );
};

export default SubscriptionUnlimitedMenuDrawer;
